import posthog from "posthog-js";
import { ConsentState } from "@shared/consent";
import { deleteCookiesWithPrefixes } from "./store";

/**
 * Applying a consent decision to the vendors we load ourselves.
 *
 * GTM-managed tags are handled by Consent Mode and container triggers (see
 * gtm.ts). Everything in this file is a script the app loads directly, which no
 * consent tool can gate for us — a bundled npm SDK like posthog-js has no
 * <script> tag for a blocker to intercept, which is exactly why it kept
 * capturing under the old CookieYes setup.
 */

const REWARDFUL_API_KEY = "a0bc0f";
const REWARDFUL_SRC = "https://r.wdfl.co/rw.js";

/** First-party cookies written by analytics vendors, cleared on withdrawal. */
const ANALYTICS_COOKIE_PREFIXES = ["_ga", "_gid", "_gat", "ph_"];

/** First-party cookies written by advertising and affiliate vendors. */
const MARKETING_COOKIE_PREFIXES = [
  "_fbp",
  "_fbc",
  "_gcl_",
  "_ttp",
  "_uetsid",
  "_uetvid",
  "rewardful",
];

let rewardfulRequested = false;

/**
 * Whether PostHog has recorded a page view for the current page load.
 *
 * PostHog's automatic pageview fires during init. A first-time visitor in an
 * opt-out region is initialised opted out (their regime is only known once the
 * geo call returns), so that pageview is dropped and the visit would never
 * appear. Replaying it on opt-in closes the gap; the flag stops it being
 * replayed a second time if consent is toggled again later.
 */
let pageviewCaptured = false;

export function markPostHogPageviewCaptured(): void {
  pageviewCaptured = true;
}

function applyPostHog(granted: boolean): void {
  if (granted) {
    // Move off memory persistence first, so the distinct_id generated during
    // the opted-out window is carried into the cookie rather than replaced.
    // That continuity is what keeps a landing-page experiment variant stable
    // across the hop to app.outclose.com.
    posthog.set_config({ persistence: "localStorage+cookie" });
    if (posthog.has_opted_out_capturing()) {
      posthog.opt_in_capturing();
    }
    if (!pageviewCaptured) {
      pageviewCaptured = true;
      posthog.capture("$pageview");
    }
    return;
  }

  const wasCapturing = !posthog.has_opted_out_capturing();
  if (wasCapturing) {
    posthog.opt_out_capturing();
    // opt_out_capturing() stops new writes but leaves what is already stored,
    // and a withdrawal has to actually remove it. Only on a real withdrawal
    // though: resetting on every pre-consent page load would churn a fresh
    // distinct_id for visitors who have simply not answered the banner yet.
    posthog.reset();
  }
  posthog.set_config({ persistence: "memory" });
  // Runs unconditionally so cookies left behind by the previous consent tool
  // are cleaned up on the first load after this ships.
  deleteCookiesWithPrefixes(ANALYTICS_COOKIE_PREFIXES);
}

function applyRewardful(granted: boolean): void {
  if (!granted) {
    deleteCookiesWithPrefixes(MARKETING_COOKIE_PREFIXES);
    return;
  }
  if (rewardfulRequested) return;
  rewardfulRequested = true;

  // The queue stub in index.html has been buffering calls; loading rw.js now
  // flushes them.
  const script = document.createElement("script");
  script.async = true;
  script.src = REWARDFUL_SRC;
  script.dataset.rewardful = REWARDFUL_API_KEY;
  document.head.appendChild(script);
}

let lastApplied: { analytics: boolean; marketing: boolean } | null = null;

/**
 * Brings every directly-loaded vendor in line with `state`.
 *
 * Only categories that actually changed are re-applied. Re-running the denied
 * branch on every page load would call `posthog.reset()` each time and churn a
 * new distinct_id for visitors who are simply sitting pre-consent.
 */
export function applyConsentToVendors(state: ConsentState): void {
  if (!lastApplied || lastApplied.analytics !== state.analytics) {
    applyPostHog(state.analytics);
  }
  if (!lastApplied || lastApplied.marketing !== state.marketing) {
    applyRewardful(state.marketing);
  }
  lastApplied = { analytics: state.analytics, marketing: state.marketing };
}
